import sanitizeHtml from "sanitize-html";
import { Page } from "../../../components/templates/Page";
import { strapiService, teamTailorService } from "@services";
import Custom404 from "../../404";
import { useRouter as usePagesRouter } from "next/router";
import Blank from "../../_blank";
import styled from "styled-components";
import { color as themeColor, Article } from "@styles";
import { H1 } from "../../../components/atoms/Heading";
import { P1 } from "../../../components/atoms/Paragraph";
import { StandardPadding } from "../../../components/templates/StandardPadding";
import { Job } from "services/teamTailor/types";
import { Button } from "../../../components/atoms/Button";
import { Spacer } from "../../../components/templates/Spacer";
import { Meta } from "components/atoms/Meta";
import { BlogShareIcon } from "../../../components/blog/BlogShareIcon";
import { getPageTranslations, throttleStaticProps } from "utils/server";
import { mobile } from "components/templates/Breakpoints";

const getRemoteStatus = (status: Job["attributes"]["remote-status"], location: string) => {
  switch (status) {
    case "temporary":
      return location ? `Temporarily remote • ${location}` : "Temporarily remote";
    case "hybrid":
      return location ? `Flexible remote • ${location}` : "Flexible remote";
    case "fully":
      return "Fully remote";
    case "none":
    default:
      return location || "No remote work";
  }
};

// certain job postings require a unique application URL
const getUrl = (slug: string, iframeUrl: string) => {
  switch (slug) {
    case "2792793-health-analytics-associate-ktp":
      return "https://hrorganiser.essex.ac.uk/tlive_webrecruitment/wrd/run/ETREC107GF.open?VACANCY_ID=230747SWQZ&WVID=9918109NEm&LANG=USA";
    default:
      return iframeUrl;
  }
};

const Content = ({
  postData: { job, department, location, slug },
  err,
  latestCaseStudy,
  locale,
}) => {
  const pagesRouter = usePagesRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (pagesRouter.isFallback) {
    return <Blank />;
  }

  if (err) {
    return <Custom404 latestCaseStudy={latestCaseStudy} locale={locale} />;
  }

  const attributes = job?.attributes || {};
  const links = job?.links || {};

  if (!attributes || !links || attributes?.status === "archived") {
    return <Custom404 latestCaseStudy={latestCaseStudy} locale={locale} />;
  }

  const { title, pitch, picture, body } = attributes;

  const metadata = {
    description: pitch,
    image: picture?.thumb,
    imageAlt: title,
    noIndex: false,
    title: `${title} | Careers | YuLife`,
    url: `careers/${slug}/`,
  };

  const summary = [
    department?.attributes.name,
    getRemoteStatus(attributes["remote-status"], location?.attributes.name),
  ]
    .filter(Boolean)
    .join(" • ");

  const applyButton = (isCentered = false) => (
    <ButtonContainer isCentered={isCentered}>
      <Button url={getUrl(slug, links["careersite-job-apply-iframe-url"])}>
        {attributes["apply-button-text"] || "Apply for this job"}
      </Button>
    </ButtonContainer>
  );

  return (
    <>
      <Meta locale={locale} {...metadata} />
      <Page latestCaseStudy={latestCaseStudy} hero={null}>
        {picture?.standard ? (
          <FeatureImageWrapper>
            <FeatureImage src={picture.standard} alt={title} />
          </FeatureImageWrapper>
        ) : null}
        <TitleContainer>
          <Title align="center" fontSize="3.5rem" lineheight="4rem" pb="1.5rem">
            {title}
          </Title>
          <BlogExcerpt>{summary}</BlogExcerpt>
          <BlogExcerpt>{pitch}</BlogExcerpt>
          <Centered>
            <BlogShareIcon title={title} slug={`${slug}/`} contentType="vacancy" />
          </Centered>
        </TitleContainer>
        {applyButton(true)}
        <Article
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(body, {
              allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
            }),
          }}
        />
        {applyButton(false)}
        <Spacer height="3rem" />
        <BlogArticlePadding>
          <BlogShareIcon title={title} slug={`${slug}/`} textVersion contentType="vacancy" />
        </BlogArticlePadding>{" "}
        <Spacer height="5rem" />
      </Page>
    </>
  );
};

export const getStaticPaths = async () => {
  const jobs = await teamTailorService.getJobs();

  const ukPaths = jobs.map(({ links }) => ({
    slug: teamTailorService.getSlugFromJobUrl(links),
    locale: "uk",
  }));

  const paths = strapiService.formatResourceSlugsForStaticPaths(ukPaths, true); // TODO - generalise this function?!

  return { paths, fallback: process.env.ENV_NAME === "local" ? false : "blocking" };
};

export const getStaticProps = async ({ params: { slug }, locale }) => {
  const id = slug.split("-")[0];
  const job = await teamTailorService.getJob(id);

  if (!job) {
    throw new Error(`TeamTailor API service is not responding for job ID ${id}.`);
  }

  const department = (await teamTailorService.getDepartment(id)) || null;
  const location = (await teamTailorService.getLocation(id)) || null;

  const postData = { job, department, location, slug };

  const genericProps = await strapiService.getGenericProps(locale, {
    hasKeyResources: false,
    hasLatestCaseStudy: true,
  });

  return throttleStaticProps(
    {
      props: {
        postData,
        err: null,
        messages: await getPageTranslations(locale),
        ...genericProps,
      },
      revalidate: 300,
    },
    5000,
    true,
  );
};

export default Content;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1.5rem 0 -1rem;
`;

const BlogArticlePadding = styled(StandardPadding)`
  max-width: 50rem;
  margin: 0 auto;
`;

const FeatureImageWrapper = styled.div`
  width: 100%;
  max-width: 70rem;
  height: auto;
  max-height: 29.5rem;
  margin: 0 auto 4rem auto;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0.5rem 0.5rem 0 #f5f5f5;
  border-radius: 0.5rem;

  img {
    border-radius: 0.5rem;
  }

  @media only screen and (max-width: 70rem) {
    border-radius: 0;

    img {
      border-radius: 0;
    }
  }

  margin-top: 7.5rem;

  @media only screen and (max-width: 65rem) {
    margin-top: 5rem;
  }

  @media only screen and (max-width: 30rem) {
    margin: 2.5rem 0 2.5rem auto;
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  max-width: 70rem;
  object-fit: cover;
  border-radius: 0.5rem;
  height: auto;
  max-height: 29.5rem;
  border: 0.125rem solid #e3e3e1;
  pointer-events: none;

  @media only screen and (max-width: 70rem) {
    border-radius: 0;
  }
`;

const ButtonContainer = styled(StandardPadding)<{ isCentered: boolean }>`
  max-width: 50rem;
  margin: 3rem auto 3rem;
  display: flex;
  justify-content: ${(p) => (p.isCentered ? "center" : "left")};
  align-items: center;
`;

const TitleContainer = styled(StandardPadding)`
  max-width: 62rem;
  margin: 0 auto;
`;

const customTextSelection = `
  ::-moz-selection {
    background: ${themeColor.P20};
  }
  ::selection {
    background: ${themeColor.P20};
  }
`;

const BlogExcerpt = styled(P1)`
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 2rem;
  text-align: center;
  letter-spacing: 0.075rem;
  color: #5c5757;
  ${customTextSelection};
`;

const Title = styled(H1)`
  ${mobile} {
    font-size: 3rem;
    line-height: 3.5rem;
  }

  @media only screen and (max-width: 30rem) {
    font-size: 2.5rem;
    line-height: 3rem;
  }
`;
